import { useZero } from "@rocicorp/zero/react";
import { styled } from "goober";
import {
  useEffect,
  useRef,
  useState,
  type MouseEvent as ReactMouseEvent,
} from "react";
import { sharedMutators } from "../../../shared/mutators";
import { queries } from "../../../shared/queries";
import { parseSkillMd, SKILL_MD_PATH } from "../../../shared/skills/spec";
import { useHasAccess } from "../../hooks/useHasAccess";
import { useQuery } from "../../hooks/useQuery";
import { makeUrl, navigate, routes, useCurrentUrl } from "../../lib/urls";
import { AccessMapPanel } from "../AccessMapPanel";
import { Button } from "../Button";
import { ConfirmModal } from "../ConfirmModal";
import { ConnectorLogoContainer } from "../ConnectorLogoContainer";
import { DangerSection } from "../Settings/DangerSection";
import { Icon } from "../Icon";
import { Modal } from "../Modal";
import {
  useScopeAccessCloseWarning,
  WarningContent,
  WarningFooter,
} from "../ScopeAccessCloseWarning";
import { TabLinkButton } from "../TabButton";
import { Text } from "../Text";
import { AvailabilityCard } from "./AvailabilityCard";
import { GitSourceCard } from "./GitSourceCard";
import { SettingsTab, type SkillForm } from "./SettingsTab";
import { SkillAccessTab } from "./SkillAccessTab";
import { SkillFilesPanel, type SkillFileEditsHandle } from "./SkillFilesPanel";

type Tab = "settings" | "files" | "access";

const Header = styled("div")`
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 12px 16px;
`;

const HeaderLeft = styled("div")`
  flex: 1;
  min-width: 0;
  display: flex;
  align-items: center;
  gap: 8px;
`;

const Body = styled("div")`
  padding: 16px;
  flex: 1;
  border: 0.5px solid var(--border-alpha);
  border-left: 0;
  border-right: 0;
  overflow: auto;
`;

const Footer = styled("div")`
  padding: 12px 16px;
  display: flex;
  justify-content: space-between;
  align-items: center;
`;

const FooterRight = styled("div")`
  display: flex;
  align-items: center;
  gap: 8px;
`;

const Tabs = styled("div")`
  display: flex;
  flex-shrink: 0;
  gap: 8px;
`;

const HeaderRight = styled("div")`
  flex: 1;
  display: flex;
  align-items: center;
  justify-content: flex-end;
  gap: 6px;
`;

const SettingsBody = styled("div")`
  display: flex;
  flex-direction: column;
  gap: 16px;
`;

type Props = {
  id: string;
  onClose: () => void;
};

export function SkillModal({ id, onClose }: Props) {
  const z = useZero();
  const [skill] = useQuery(queries.loadSkill({ skillId: id }));
  const canWrite = useHasAccess("skill:write", skill?.id);

  const { tab } = useCurrentUrl(routes.modal.skill, { id });
  const activeTab = tab as Tab;
  const setActiveTab = (next: Tab) =>
    navigate(routes.modal.skill, { id, tab: next });

  const [form, setForm] = useState<SkillForm | null>(null);
  const [isDirty, setIsDirty] = useState(false);
  const [confirmDiscard, setConfirmDiscard] = useState(false);
  const [selectedPath, setSelectedPath] = useState(SKILL_MD_PATH);
  const [savePrompt, setSavePrompt] = useState<{ proceed: () => void } | null>(
    null
  );
  const fileEditsRef = useRef<SkillFileEditsHandle | null>(null);
  const seededFor = useRef<string | null>(null);
  const { stagedGrantsRef, requestClose, closeWarningModal } =
    useScopeAccessCloseWarning({
      singular: "member or group",
      plural: "members and groups",
    });

  useEffect(() => {
    if (skill && seededFor.current !== skill.id) {
      seededFor.current = skill.id;
      setForm({
        name: skill.name,
        description: skill.description,
        instructions: skill.instructions,
        enabled: skill.enabled ?? true,
      });
    }
  }, [skill]);

  if (!skill || !form) return null;

  const fresh = skill.fresh;
  const gitDraft = fresh && skill.gitInstallation !== undefined;

  const applySkillMd = (content: string) => {
    const parsed = parseSkillMd(content);
    setForm(
      (prev) =>
        prev && {
          ...prev,
          name: parsed.manifest.name,
          description: parsed.manifest.description,
          instructions: parsed.instructions,
        }
    );
    setIsDirty(true);
  };

  const setEnabled = (enabled: boolean) => {
    if (!canWrite) return;
    const next = { ...form, enabled };
    setForm(next);
    setIsDirty(true);
    z.mutate(
      sharedMutators.updateSkill({
        skillId: id,
        name: next.name,
        description: next.description,
        instructions: next.instructions,
        enabled,
      })
    );
  };

  const discardDraft = () => {
    z.mutate(sharedMutators.deleteSkill({ skillId: id }));
    onClose();
  };

  const dismiss = () => {
    if (fresh && canWrite && !gitDraft) {
      if (isDirty) setConfirmDiscard(true);
      else discardDraft();
      return;
    }
    onClose();
  };

  const saveAndClose = async () => {
    if ((await fileEditsRef.current?.save()) === false) return;
    await z.mutate(sharedMutators.publishSkill({ skillId: id })).client;
    onClose();
  };

  // A dirty file draft would be lost on close; prompt to save it first (fresh
  // skills route through publish/discard, which handle the draft themselves).
  const guardEdits = (proceed: () => void) => {
    if (!fresh && fileEditsRef.current?.hasUnsavedChanges) {
      setSavePrompt({ proceed });
      return;
    }
    requestClose(proceed);
  };

  const close = () => guardEdits(dismiss);
  const finish = () =>
    guardEdits(canWrite && fresh && !gitDraft ? saveAndClose : dismiss);

  const saveFileDraft = async (next: Tab) => {
    const edits = fileEditsRef.current;
    if (edits?.isSaving) return;
    if ((await edits?.save()) === false) return;
    setActiveTab(next);
  };

  const guardTabMouseDown =
    (next: Tab) => (event: ReactMouseEvent<HTMLAnchorElement>) => {
      const edits = fileEditsRef.current;
      if (
        activeTab !== "files" ||
        next === "files" ||
        !edits?.hasUnsavedChanges ||
        event.button !== 0 ||
        event.ctrlKey ||
        event.metaKey
      ) {
        return;
      }
      event.preventDefault();
      void saveFileDraft(next);
    };

  const guardTabClick =
    (next: Tab) => (event: ReactMouseEvent<HTMLAnchorElement>) => {
      if (
        event.detail !== 0 ||
        activeTab !== "files" ||
        next === "files" ||
        !fileEditsRef.current?.hasUnsavedChanges
      ) {
        return;
      }
      event.preventDefault();
      void saveFileDraft(next);
    };

  return (
    <>
      <Modal
        open
        onClose={close}
        style={{
          width: 680,
          height: 760,
          display: "flex",
          flexDirection: "column",
        }}
      >
        <Header>
          <HeaderLeft>
            <ConnectorLogoContainer
              size={32}
              borderRadius={8}
              hoverBackground={false}
            >
              <Icon name="graduation-cap" size={16} />
            </ConnectorLogoContainer>
            <Text variant="text-regular-md" ellipsis>
              {gitDraft
                ? "Installing skill"
                : fresh
                  ? "New skill"
                  : skill.name || "Skill"}
            </Text>
          </HeaderLeft>
          <Tabs>
            <TabLinkButton
              to={makeUrl(routes.modal.skill, { id, tab: "settings" })}
              className={activeTab === "settings" ? "active" : undefined}
              onMouseDown={guardTabMouseDown("settings")}
              onClick={guardTabClick("settings")}
            >
              <Icon name="cog" size={16} />
              <Text variant="text-small-md">Settings</Text>
            </TabLinkButton>
            <TabLinkButton
              to={makeUrl(routes.modal.skill, { id, tab: "files" })}
              className={activeTab === "files" ? "active" : undefined}
              onMouseDown={guardTabMouseDown("files")}
              onClick={guardTabClick("files")}
            >
              <Icon name="notebook-text" size={16} />
              <Text variant="text-small-md">Files</Text>
            </TabLinkButton>
            <TabLinkButton
              to={makeUrl(routes.modal.skill, { id, tab: "access" })}
              className={activeTab === "access" ? "active" : undefined}
              onMouseDown={guardTabMouseDown("access")}
              onClick={guardTabClick("access")}
            >
              <Icon name="lock" size={16} />
              <Text variant="text-small-md">Access</Text>
            </TabLinkButton>
          </Tabs>
          <HeaderRight>
            <Button size="l" variant="secondary" icon="x" onClick={close} />
          </HeaderRight>
        </Header>
        <Body>
          {activeTab === "access" ? (
            <div style={{ display: "flex", flexDirection: "column", gap: 16 }}>
              {!fresh && (
                <AccessMapPanel
                  nodeId={skill.id}
                  noun="skill"
                  guard={requestClose}
                />
              )}
              <SkillAccessTab
                skill={skill}
                canManage={canWrite}
                onEdit={() => setIsDirty(true)}
                stagedGrantsRef={stagedGrantsRef}
              />
            </div>
          ) : activeTab === "files" ? (
            <SkillFilesPanel
              skillId={skill.id}
              disabled={!canWrite || gitDraft}
              selectedPath={selectedPath}
              onSelectPath={setSelectedPath}
              onChange={() => setIsDirty(true)}
              onSkillMdChange={applySkillMd}
              editsRef={fileEditsRef}
            />
          ) : (
            <SettingsBody>
              {skill.gitInstallation && (
                <GitSourceCard
                  skillId={skill.id}
                  canWrite={canWrite}
                  installation={skill.gitInstallation}
                />
              )}
              <SettingsTab
                form={form}
                onEditFile={() => {
                  setSelectedPath(SKILL_MD_PATH);
                  setActiveTab("files");
                }}
              />
              <AvailabilityCard
                enabled={form.enabled}
                disabled={!canWrite || gitDraft}
                onToggle={() => setEnabled(!form.enabled)}
              />
              {canWrite && !fresh && (
                <DangerSection
                  title="Delete skill"
                  description="Permanently deletes this skill and its access settings."
                  actionLabel="Delete skill"
                  confirm={{
                    confirmLabel: "Delete",
                    onConfirm: () => {
                      z.mutate(sharedMutators.deleteSkill({ skillId: id }));
                      onClose();
                    },
                  }}
                />
              )}
            </SettingsBody>
          )}
        </Body>
        <Footer>
          <div />
          <FooterRight>
            {canWrite && fresh && !gitDraft && activeTab !== "access" ? (
              <Button
                variant="primary"
                size="l"
                onClick={() =>
                  void saveFileDraft(
                    activeTab === "settings" ? "files" : "access"
                  )
                }
              >
                Next
              </Button>
            ) : (
              <Button variant="primary" size="l" onClick={finish}>
                {canWrite && fresh && !gitDraft ? "Save" : "Done"}
              </Button>
            )}
          </FooterRight>
        </Footer>
      </Modal>
      {closeWarningModal}
      {savePrompt && (
        <Modal open onClose={() => setSavePrompt(null)} alert>
          <WarningContent>
            <Text variant="text-regular-md" color="var(--text-primary)">
              Save before closing?
            </Text>
            <Text variant="text-regular" color="var(--text-secondary)">
              You have unsaved changes.
            </Text>
            <WarningFooter>
              <Button
                size="l"
                variant="secondary"
                onClick={() => {
                  const { proceed } = savePrompt;
                  setSavePrompt(null);
                  requestClose(proceed);
                }}
              >
                Discard
              </Button>
              <Button
                size="l"
                onClick={async () => {
                  const { proceed } = savePrompt;
                  if ((await fileEditsRef.current?.save()) === false) return;
                  setSavePrompt(null);
                  requestClose(proceed);
                }}
              >
                Save and close
              </Button>
            </WarningFooter>
          </WarningContent>
        </Modal>
      )}
      <ConfirmModal
        open={confirmDiscard}
        onClose={() => setConfirmDiscard(false)}
        onConfirm={discardDraft}
        title="Discard new skill?"
        description="Your changes will be lost if you continue."
        confirmLabel="Discard"
        destructive
      />
    </>
  );
}
